import {
  CheckCircleOutlined,
  CopyOutlined,
  DownloadOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  UserOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Input,
  Progress,
  Row,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  collectionAPIWithErrorHandling,
  collectionHelpers,
} from "../../../api/collection/collection";
import UploadModal from "../../../components/Upload/UploadModal";
import { downloadBlob } from "../../../utils/excel";
import DuplicateModal from "./DuplicateModal";
import { paymentExtendedUntil } from "./CollectionDeadline";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmAction } from "../../../components/common/confirmDialog";
import { readSwrCache, writeSwrCache, SWR_MAX_AGE_SHORT } from "../../../utils/swrCache";
import RowActions, { actionColumnDef } from "../../../components/common/RowActions";
import StatCard from "../../../components/common/StatCard";
import { neutral, semantic, typography } from "../../../theme/tokens";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const EMPTY_DASHBOARD = {
  total_collections: 0,
  total_customers: 0,
  total_amount: 0,
  paid_amount: 0,
  unpaid_amount: 0,
  overpaid_amount: 0,
  average_completion_rate: 0,
};

// Endpoint dashboard tổng hợp toàn bộ đợt thu khớp bộ lọc nên page/per_page không đổi
// số liệu — chỉ search/status/khoảng ngày mới cần nằm trong key cache.
const dashboardCacheKey = ({ search, status, start_date, end_date } = {}) =>
  `collections.dashboard:${search || ""}|${status || ""}|${start_date || ""}|${end_date || ""}`;

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = [
  "name",
  "start_date",
  "end_date",
  "charge_target_type",
  "progress",
  "amount_summary",
  "status",
];

export default function CollectionTable() {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    from: 0,
    to: 0,
    lastPage: 1,
  });
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    sort_by: "created_at",
    sort_order: "desc",
    start_date: "",
    end_date: "",
  });

  // SWR: seed mini dashboard từ cache của phiên (bộ lọc mặc định — đúng bộ lọc mà
  // useEffect lúc mount dùng) để 4 card có số ngay, không hiện 0 rồi mới nhảy.
  const [dashboard, setDashboard] = useState(
    () =>
      readSwrCache(dashboardCacheKey({}), { maxAge: SWR_MAX_AGE_SHORT }) ??
      EMPTY_DASHBOARD
  );

  const [isExporting, setIsExporting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);

  const [dateRange, setDateRange] = useState([null, null]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const { user } = useSelector((state) => state.persistedReducer.user);
  const userType = user?.data.info.type;

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  const handleDuplicate = (record) => {
    setSelectedCollection(record);
    setDuplicateModalVisible(true);
  };

  const handleDuplicateSuccess = () => {
    setDuplicateModalVisible(false);
    setSelectedCollection(null);
    // Refresh data
    fetchCollections(
      pagination.current,
      pagination.pageSize,
      searchText,
      filters.status
    );
  };

  // Fetch collections data using the proper API
  const fetchCollections = async (
    page = 1,
    perPage = 10,
    search = "",
    status = "",
    customDateRange = dateRange
  ) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        search: search.trim(),
        status,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
        start_date: filters.start_date,
        end_date: filters.end_date,
      };

      // Thêm date range vào params nếu có
      if (customDateRange[0] && customDateRange[1]) {
        params.start_date = customDateRange[0].format("YYYY-MM-DD");
        params.end_date = customDateRange[1].format("YYYY-MM-DD");
      }

      const response = await collectionAPIWithErrorHandling.getCollections(params);
      const { collections: collectionData, pagination: paginationData } =
        collectionHelpers.extractCollectionList(response);

      const mappedData = collectionData.map((item) => ({
        ...item,
        childRecords: item.children, // Đổi tên children
        children: undefined, // Xóa children gốc
      }));

      setCollections(mappedData);
      setPagination({
        current: paginationData.current,
        pageSize: paginationData.pageSize,
        total: paginationData.total,
        from: paginationData.from,
        to: paginationData.to,
        lastPage: paginationData.lastPage,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách đợt thu");
      console.error("Error fetching collections:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async (
    page = 1,
    perPage = 10,
    search = "",
    status = "",
    customDateRange = dateRange
  ) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        search: search.trim(),
        status,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
        start_date: filters.start_date,
        end_date: filters.end_date,
      };

      // Thêm date range vào params nếu có
      if (customDateRange[0] && customDateRange[1]) {
        params.start_date = customDateRange[0].format("YYYY-MM-DD");
        params.end_date = customDateRange[1].format("YYYY-MM-DD");
      }

      const response = await collectionAPIWithErrorHandling.getDashboard(params);
      const dashboardData = response?.data?.data;
      // Chỉ ghi khi thực sự có dữ liệu: trước đây set thẳng response?.data?.data nên
      // response lạ là dashboard thành undefined và các card đọc thuộc tính trên undefined.
      if (dashboardData) {
        setDashboard(dashboardData);
        writeSwrCache(dashboardCacheKey(params), dashboardData);
      }
    } catch (error) {
      message.error(error.message || "Không thể tải thống kê đợt thu");
      console.error("Error fetching collections:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchCollections();
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle table pagination change
  const handleTableChange = (paginationConfig, tableFilters, sorter) => {
    // Handle sorting
    if (sorter && sorter.field) {
      const sortBy = sorter.field;
      const sortOrder = sorter.order === "descend" ? "desc" : "asc";
      setFilters((prev) => ({
        ...prev,
        sort_by: sortBy,
        sort_order: sortOrder,
      }));
    }

    // Handle status filter
    let statusFilter = "";
    if (tableFilters && tableFilters.status && tableFilters.status.length > 0) {
      statusFilter = tableFilters.status[0];
    }

    fetchCollections(
      paginationConfig.current,
      paginationConfig.pageSize,
      searchText,
      statusFilter
    );
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchText(value);
    fetchCollections(1, pagination.pageSize, value, filters.status);
    fetchDashboard(1, pagination.pageSize, value, filters.status);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchText("");
    fetchCollections(1, pagination.pageSize, "", filters.status);
    fetchDashboard(1, pagination.pageSize, "", filters.status);
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates || [null, null]);
    fetchCollections(1, pagination.pageSize, searchText, filters.status, dates || [null, null]);
    fetchDashboard(1, pagination.pageSize, searchText, filters.status, dates || [null, null]);
  };

  // Xoá toàn bộ bộ lọc
  const handleReset = () => {
    setSearchText("");
    setDateRange([null, null]);
    fetchCollections(1, pagination.pageSize, "", filters.status, [null, null]);
    fetchDashboard(1, pagination.pageSize, "", filters.status, [null, null]);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  // Get status tag
  const getStatusTag = (status) => {
    const statusConfig = {
      PLANNED: { color: "gray", text: "Nháp" },
      ONGOING: { color: "green", text: "Đang diễn ra" },
      CLOSED: { color: "red", text: "Đã kết thúc" },
    };

    const config = statusConfig[status] || { color: "default", text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getChargeTargetTypeTag = (charge_target_type) => {
    const statusConfig = {
      CUSTOMER: { color: "warning", text: "Khách hàng" },
      APARTMENT: { color: "green", text: "Chung cư" },
      VEHICLE: { color: "red", text: "Bãi xe" },
    };

    const config = statusConfig[charge_target_type] || { color: "default", text: charge_target_type };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // Action handlers
  const handleInfo = (record) => {
    message.info(`Chi tiết thông tin đợt thu: ${record.name}`);
    navigate(`/collections/information/${record.id}`);
  };

  const handleInfoParent = (record) => {
    message.info(`Chi tiết thông tin đợt thu: ${record.name}`);
    navigate(`/collections/detail/${record.id}`);
  };

  const handleUploadSuccess = (results) => {
    console.log("Upload results:", results);
    message.success(`Upload thành công ${results.length} file(s)!`);
  };

  const handleToggleStatus = async (record, status) => {
    try {
      const action = status === "ONGOING" ? "kích hoạt" : "tạm dừng";
      const confirmed = await confirmAction({
        title: `Xác nhận ${action}`,
        content: `Bạn có chắc chắn muốn ${action} đợt thu "${record.name}"?`,
      });
      if (!confirmed) return;

      await collectionAPIWithErrorHandling.toggleCollectionStatus(record.id, status);
      message.success(`${action.charAt(0).toUpperCase() + action.slice(1)} đợt thu thành công`);

      // Refresh data
      fetchCollections(
        pagination.current,
        pagination.pageSize,
        searchText,
        filters.status
      );
    } catch (error) {
      message.error(error.message || "Không thể thay đổi trạng thái đợt thu");
    }
  };

  const handleAdd = () => {
    message.info("Thêm đợt thu mới");
    navigate("/store-collection");
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await collectionAPIWithErrorHandling.exportCollections();

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const currentDate = new Date().toISOString().split("T")[0].replace(/-/g, "_");
      const filename = `DOT_THU_${currentDate}.xlsx`;

      downloadBlob(blob, filename);
      message.success("Tải xuống danh sách đợt thu thành công!");
    } catch (error) {
      message.error(error.message || "Không thể tải xuống danh sách đợt thu");
    } finally {
      setIsExporting(false);
    }
  };

  // Cột bảng con (đơn vị con của đợt thu)
  const childColumns = [
    {
      title: "STT",
      key: "index",
      width: 50,
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Đơn vị",
      key: "nameOrganization",
      width: 150,
      render: (_, record) => (
        <div>
          <Text strong>{record.organization?.name}</Text>
        </div>
      ),
    },
    {
      // Tiến độ theo KHOẢN THU (không tính khoản đã hủy) — khớp nút lọc "Đã TT" ở chi tiết.
      title: "Khoản thu",
      key: "paid_customers",
      width: 140,
      align: "center",
      render: (_, record) => {
        const stats = record.stats;
        const total = stats.totalFees ?? 0;
        const paid = stats.paidFees ?? 0;
        const rate = total > 0 ? Math.round((paid / total) * 100) : 0;
        return (
          <div style={{ minWidth: 100 }} title="Khoản đã thanh toán / tổng khoản thu (không tính khoản đã hủy)">
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
              <span style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 600, color: "#128555" }}>{paid}</span>
                <span style={{ color: "#64748B" }}>/{total}</span>
              </span>
              <span style={{ fontSize: 11, color: rate >= 80 ? "#128555" : rate >= 50 ? "#A8681E" : "#E5065C", fontWeight: 600 }}>{rate}%</span>
            </div>
            <Progress
              percent={rate}
              size="small"
              showInfo={false}
              strokeColor={rate >= 80 ? "#128555" : rate >= 50 ? "#A8681E" : "#E5065C"}
              trailColor="#F1F5F9"
            />
          </div>
        );
      },
    },
    {
      title: "Tiền thu",
      key: "amount_collected",
      width: 150,
      align: "right",
      render: (_, record) => {
        const stats = record.stats;
        return (
          <div style={{ lineHeight: 1.4, fontVariantNumeric: "tabular-nums" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#128555" }}>{formatCurrency(stats.paidAmount)}</div>
            <div style={{ fontSize: 11, color: "#64748B" }}>/ {formatCurrency(stats.totalAmount)}</div>
          </div>
        );
      },
    },
    {
      title: "Thao tác",
      key: "actions",
      width: 100,
      fixed: "right",
      align: "center",
      render: (_, record) => (
        <RowActions
          actions={[
            { key: "info", label: "Xem chi tiết", icon: <InfoCircleOutlined />, tone: "view", onClick: () => handleInfo(record) },
          ]}
        />
      ),
    },
  ];

  // ===== Định nghĩa cột chính =====
  const columnDefs = [
    ...(userType === "SHOP" || userType === "WARD_LEADER"
      ? [{
          key: "index", label: "STT", fixed: true,
          column: { title: "STT", key: "index", width: 50, align: "center", render: (_, __, index) => index + 1 },
        }]
      : []),
    {
      key: "code", label: "Mã đợt thu", fixed: true,
      column: {
        title: "Mã đợt thu", dataIndex: "code", key: "code", width: 100, fixed: "left",
        render: (text) => <strong className="text-blue-600">{text}</strong>,
      },
    },
    {
      key: "name", label: "Tên đợt thu",
      column: {
        title: "Tên đợt thu", dataIndex: "name", key: "name", width: 200, sorter: true,
        render: (text, record) => {
          if (!record.end_date || record.status === "CLOSED") return <span>{text}</span>;
          const daysLeft = dayjs(record.end_date).diff(dayjs(), "day");
          return (
            <div>
              <div style={{ fontWeight: 500 }}>{text}</div>
              {daysLeft < 0 && (
                <span style={{ fontSize: 11, color: "#E5065C", fontWeight: 600 }}>🔥 Quá hạn {Math.abs(daysLeft)} ngày</span>
              )}
              {daysLeft >= 0 && daysLeft <= 3 && (
                <span style={{ fontSize: 11, color: "#E5065C", fontWeight: 600 }}>⚠ Còn {daysLeft} ngày</span>
              )}
              {daysLeft > 3 && daysLeft <= 7 && (
                <span style={{ fontSize: 11, color: "#A8681E", fontWeight: 600 }}>⏱ Còn {daysLeft} ngày</span>
              )}
            </div>
          );
        },
      },
    },
    {
      key: "start_date", label: "Bắt đầu",
      column: {
        title: "Bắt đầu", key: "start_date", width: 100,
        render: (_, record) => <div className="text-sm">{formatDate(record.start_date)}</div>,
      },
    },
    {
      key: "end_date", label: "Kết thúc",
      column: {
        title: "Kết thúc", key: "end_date", width: 100,
        render: (_, record) => {
          const extended = paymentExtendedUntil(record);
          return (
            <div className="text-sm">
              {formatDate(record.end_date)}
              {extended && (
                <div className="text-xs text-info-text">Gia hạn: {extended.format("DD/MM/YYYY")}</div>
              )}
            </div>
          );
        },
      },
    },
    {
      key: "charge_target_type", label: "Nhóm đối tượng",
      column: {
        title: "Nhóm đối tượng", dataIndex: "charge_target_type", key: "charge_target_type",
        width: 120, align: "left",
        render: (charge_target_type) => getChargeTargetTypeTag(charge_target_type),
        filters: [
          { text: "Khách hàng", value: "CUSTOMER" },
          { text: "Chung cư", value: "APARTMENT" },
          { text: "Bãi xe", value: "VEHICLE" },
        ],
        filterMultiple: false,
      },
    },
    {
      // Tiến độ theo KHOẢN THU (không tính khoản đã hủy) — khớp nút lọc "Đã TT" ở chi tiết.
      key: "progress", label: "Khoản thu",
      column: {
        title: "Khoản thu", key: "progress", width: 180, align: "center",
        render: (_, record) => {
          const stats = record.stats;
          const total = stats.totalFees ?? 0;
          const paid = stats.paidFees ?? 0;
          const rate = total > 0 ? Math.round((paid / total) * 100) : 0;
          return (
            <div style={{ minWidth: 120 }} title="Khoản đã thanh toán / tổng khoản thu (không tính khoản đã hủy)">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                <span style={{ fontSize: 12, color: "#475569" }}>
                  <span style={{ fontWeight: 600, color: "#128555" }}>{paid}</span>
                  <span style={{ color: "#64748B" }}>/{total}</span>
                </span>
                <span style={{ fontSize: 11, color: rate >= 80 ? "#128555" : rate >= 50 ? "#A8681E" : "#E5065C", fontWeight: 600 }}>
                  {rate}%
                </span>
              </div>
              <Progress
                percent={rate}
                size="small"
                showInfo={false}
                strokeColor={rate >= 80 ? "#128555" : rate >= 50 ? "#A8681E" : "#E5065C"}
                trailColor="#F1F5F9"
              />
            </div>
          );
        },
      },
    },
    {
      key: "amount_summary", label: "Tiền thu",
      column: {
        title: "Tiền thu", key: "amount_summary", width: 180, align: "right",
        render: (_, record) => {
          const stats = record.stats;
          return (
            <div style={{ lineHeight: 1.4, fontVariantNumeric: "tabular-nums" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#128555" }}>
                {formatCurrency(stats.paidAmount)}
              </div>
              <div style={{ fontSize: 11, color: "#64748B" }}>
                / {formatCurrency(stats.totalAmount)}
              </div>
            </div>
          );
        },
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái", dataIndex: "status", key: "status", width: 100, align: "center",
        render: (status) => getStatusTag(status),
        filters: [
          { text: "Nháp", value: "PLANNED" },
          { text: "Đang diễn ra", value: "ONGOING" },
          { text: "Đã kết thúc", value: "CLOSED" },
        ],
        filterMultiple: false,
      },
    },
    actionColumnDef((record) => {
      const isParent = record.type_child === "PARENT";

      return [
        {
          key: "info", label: "Xem chi tiết", icon: <InfoCircleOutlined />, tone: "view",
          onClick: () => {
            // Nếu có children, gọi handleInfoParent
            if (record.childRecords && record.childRecords.length > 0) {
              handleInfoParent(record);
            } else {
              handleInfo(record);
            }
          },
        },
        {
          key: "duplicate", label: "Sao chép", icon: <CopyOutlined />, tone: "edit",
          hidden: !isParent,
          onClick: () => handleDuplicate(record),
        },
        {
          key: "activate", label: "Kích hoạt", icon: <PlayCircleOutlined />, tone: "success",
          hidden: !(isParent && record.status === "PLANNED"),
          onClick: () => handleToggleStatus(record, "ONGOING"),
        },
        {
          key: "pause", label: "Tạm dừng", icon: <PauseCircleOutlined />, tone: "warning",
          hidden: !(isParent && ["PLANNED", "ONGOING"].includes(record.status)),
          onClick: () => handleToggleStatus(record, "CLOSED"),
        },
      ];
    }),
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Input.Search
          placeholder="Tìm kiếm theo tên đợt thu, mã đợt thu..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          onClear={handleClearSearch}
          enterButton={<SearchOutlined />}
          allowClear
        />
      ),
    },
    {
      key: "date_range", label: "Khoảng ngày", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <RangePicker
          style={{ width: "100%" }}
          format="DD/MM/YYYY"
          placeholder={["Từ ngày", "Đến ngày"]}
          value={dateRange}
          onChange={handleDateRangeChange}
        />
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!(searchText || (dateRange[0] && dateRange[1]));

  return (
    <BrandedPageLayout
      title="Đợt thu"
      subtitle="Quản lý các đợt thu, theo dõi tiến độ và số tiền đã thu."
      breadcrumbItems={[{ title: "Đợt thu" }, { title: "Danh sách" }]}
    >
      {/* Mini Dashboard - 4 card */}
      <Row gutter={[16, 16]} className="mb-4">
        {/* Card 1: Tổng ĐỢT thu — total_collections đếm số đợt, không phải số khoản thu của khách */}
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<FileTextOutlined />}
            tone="brand"
            label="Tổng đợt thu"
            value={dashboard.total_collections ?? 0}
          />
        </Col>

        {/* Card 3: Tổng khách hàng */}
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<UserOutlined />}
            tone="info"
            label="Tổng khách hàng"
            value={dashboard.total_customers ?? 0}
          />
        </Col>

        {/* Card 2: Tổng phải thu */}
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<WalletOutlined />}
            tone="success"
            label="Tổng phải thu"
            value={`${Number(dashboard.total_amount ?? 0).toLocaleString("vi-VN")} ₫`}
            extra={
              <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap", fontSize: typography.size.sm }}>
                <span style={{ color: semantic.success, fontWeight: typography.weight.semibold }}>
                  Đã thu {Number(dashboard.paid_amount ?? 0).toLocaleString("vi-VN")} ₫
                </span>
                <span style={{ color: semantic.danger, fontWeight: typography.weight.semibold }}>
                  Còn lại {Number(dashboard.unpaid_amount ?? 0).toLocaleString("vi-VN")} ₫
                </span>
                {/* Chỉ hiện khi thực sự có tiền thu vượt — không thêm nhiễu khi = 0. */}
                {Number(dashboard.overpaid_amount ?? 0) > 0 && (
                  <span style={{ color: semantic.warning, fontWeight: typography.weight.semibold }}>
                    Thu vượt {Number(dashboard.overpaid_amount).toLocaleString("vi-VN")} ₫
                  </span>
                )}
              </div>
            }
          />
        </Col>

        {/* Card 4: Tỷ lệ hoàn thành */}
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<CheckCircleOutlined />}
            tone="success"
            label="Tỷ lệ hoàn thành"
            value={`${dashboard.average_completion_rate ?? 0}%`}
            extra={
              <Progress
                percent={dashboard.average_completion_rate ?? 0}
                showInfo={false}
                strokeColor={
                  (dashboard.average_completion_rate ?? 0) >= 80 ? semantic.success :
                  (dashboard.average_completion_rate ?? 0) >= 50 ? semantic.warning : semantic.danger
                }
                trailColor={neutral[200]}
                size="small"
                style={{ marginTop: 8 }}
              />
            }
          />
        </Col>
      </Row>

      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleReset}
        onReload={() => {
          fetchCollections(pagination.current, pagination.pageSize, searchText, filters.status);
          fetchDashboard(pagination.current, pagination.pageSize, searchText, filters.status);
        }}
        extra={
          <>
            <Button icon={<DownloadOutlined />} onClick={handleExport} loading={isExporting}>
              Xuất Excel
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Thêm đợt thu
            </Button>
          </>
        }
      />

      <ListTableCard
        columns={columns}
        dataSource={collections}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        emptyText="Không có đợt thu"
        rowClassName={(record, index) =>
          index % 2 === 0 ? "bg-gray-50" : "bg-white"
        }
        expandable={{
          expandedRowKeys: expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
          expandedRowRender: (record) => {
            if (!record.childRecords || record.childRecords.length === 0) {
              return null;
            }
            return (
              <Table
                columns={childColumns}
                dataSource={record.childRecords}
                rowKey="id"
                pagination={false}
                size="small"
                className="bg-blue-50"
              />
            );
          },
          rowExpandable: (record) =>
            record.childRecords && record.childRecords.length > 0,
        }}
      />

      {modalVisible && (
        <UploadModal
          visible={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={handleUploadSuccess}
          title="Upload Tài Liệu"
          acceptedFileTypes=".xlsx,.xls,.csv"
          maxFileSize={10}
        />
      )}

      {duplicateModalVisible && (
        <DuplicateModal
          visible={duplicateModalVisible}
          onCancel={() => {
            setDuplicateModalVisible(false);
            setSelectedCollection(null);
          }}
          onSuccess={handleDuplicateSuccess}
          collection={selectedCollection}
        />
      )}
    </BrandedPageLayout>
  );
}
